import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import { ProtectedRoute } from './components/common/ProtectedRoute';
import { ExternalRedirect } from './components/common/ExternalRedirect';
import ErrorBoundary from './components/common/ErrorBoundary';
import { Header } from './components/common/Header';
import { Footer } from './components/common/Footer';
import { LoadingSpinner } from './components/common/LoadingSpinner';
import { GoogleAnalytics } from './components/analytics/GoogleAnalytics';
import { DocumentHead } from './components/seo/DocumentHead';
import './App.css';
import './pages/SynesthesiaIdentifierLayout.css';

// Lazy load pages for code splitting
const HomePage = lazy(() => import('./pages/HomePage').then(module => ({ default: module.HomePage })));
const SignInPage = lazy(() => import('./pages/SignInPage').then(module => ({ default: module.SignInPage })));
const SignUpPage = lazy(() => import('./pages/SignUpPage').then(module => ({ default: module.SignUpPage })));
const ColorProfilePage = lazy(() => import('./pages/ColorProfilePage').then(module => ({ default: module.ColorProfilePage })));
const VisualizationPage = lazy(() => import('./pages/VisualizationPage').then(module => ({ default: module.VisualizationPage })));
const ProfileEditPage = lazy(() => import('./pages/ProfileEditPage').then(module => ({ default: module.ProfileEditPage })));
const SettingsPage = lazy(() => import('./pages/SettingsPage').then(module => ({ default: module.SettingsPage })));
const SavedItemsPage = lazy(() => import('./pages/SavedItemsPage').then(module => ({ default: module.SavedItemsPage })));
const PublicProfilePage = lazy(() => import('./pages/PublicProfilePage').then(module => ({ default: module.PublicProfilePage })));
const CommunityPage = lazy(() => import('./pages/CommunityPage').then(module => ({ default: module.CommunityPage })));
const AboutPage = lazy(() => import('./pages/AboutPage').then(module => ({ default: module.AboutPage })));
const LearnPage = lazy(() => import('./pages/LearnPage').then(module => ({ default: module.LearnPage })));
const TermsPage = lazy(() => import('./pages/TermsPage').then(module => ({ default: module.TermsPage })));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage').then(module => ({ default: module.PrivacyPolicyPage })));
const SynesthesiaIdentifierPage = lazy(() => import('./pages/SynesthesiaIdentifierPage').then(module => ({ default: module.SynesthesiaIdentifierPage })));
const SynesthesiaIdentifierStartPage = lazy(() => import('./pages/SynesthesiaIdentifierStartPage').then(module => ({ default: module.SynesthesiaIdentifierStartPage })));
const SynesthesiaIdentifierTriggerPage = lazy(() => import('./pages/SynesthesiaIdentifierTriggerPage').then(module => ({ default: module.SynesthesiaIdentifierTriggerPage })));
const SynesthesiaIdentifierTriggerOptionPage = lazy(() => import('./pages/SynesthesiaIdentifierTriggerOptionPage').then(module => ({ default: module.SynesthesiaIdentifierTriggerOptionPage })));
const SynesthesiaIdentifierExperiencePage = lazy(() => import('./pages/SynesthesiaIdentifierExperiencePage').then(module => ({ default: module.SynesthesiaIdentifierExperiencePage })));
const SynesthesiaIdentifierExperienceOptionPage = lazy(() => import('./pages/SynesthesiaIdentifierExperienceOptionPage').then(module => ({ default: module.SynesthesiaIdentifierExperienceOptionPage })));
// Result page loaded directly so updates always show (no cached lazy chunk)
import { SynesthesiaIdentifierResultPage } from './pages/SynesthesiaIdentifierResultPage';

function App() {
  return (
    <HelmetProvider>
      <ErrorBoundary>
        <ToastProvider>
          <AuthProvider>
            <Router>
              <DocumentHead />
              <GoogleAnalytics />
              <div className="app">
                <Header />
                <main className="main-content" role="main">
                  <Suspense fallback={<LoadingSpinner size="large" message="Loading page..." fullScreen />}>
                    <Routes>
                      <Route path="/" element={<HomePage />} />
                      <Route path="/signin" element={<SignInPage />} />
                      <Route path="/signup" element={<SignUpPage />} />
                      <Route
                        path="/color-profile"
                        element={
                          <ProtectedRoute>
                            <ColorProfilePage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/visualize"
                        element={
                          <ProtectedRoute>
                            <VisualizationPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/profile/edit"
                        element={
                          <ProtectedRoute>
                            <ProfileEditPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/settings"
                        element={
                          <ProtectedRoute>
                            <SettingsPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/saved"
                        element={
                          <ProtectedRoute>
                            <SavedItemsPage />
                          </ProtectedRoute>
                        }
                      />
                      {/* Web pages - must be before public profile route */}
                      <Route path="/about" element={<AboutPage />} />
                      <Route path="/community" element={<CommunityPage />} />
                      <Route path="/learn" element={<LearnPage />} />
                      <Route path="/synesthesia-self-diagnose" element={<SynesthesiaIdentifierPage />} />
                      <Route path="/synesthesia-self-diagnose/start" element={<SynesthesiaIdentifierStartPage />} />
                      <Route path="/synesthesia-self-diagnose/trigger" element={<SynesthesiaIdentifierTriggerPage />} />
                      <Route path="/synesthesia-self-diagnose/trigger/:triggerSlug" element={<SynesthesiaIdentifierTriggerOptionPage />} />
                      <Route path="/synesthesia-self-diagnose/experience" element={<SynesthesiaIdentifierExperiencePage />} />
                      <Route path="/synesthesia-self-diagnose/experience/:experienceSlug" element={<SynesthesiaIdentifierExperienceOptionPage />} />
                      <Route path="/synesthesia-self-diagnose/result/:triggerSlug/:experienceSlug" element={<SynesthesiaIdentifierResultPage />} />
                      <Route path="/terms" element={<TermsPage />} />
                      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                      {/* Blog redirect - must be before :username route */}
                      <Route 
                        path="/blog" 
                        element={<ExternalRedirect to="https://synesthetic.ca/blog/" />} 
                      />
                      {/* Hugo taxonomy/section paths must not fall through to /:username (soft duplicates) */}
                      <Route path="/categories" element={<ExternalRedirect to="https://synesthetic.ca/blog/" />} />
                      <Route path="/categories/*" element={<ExternalRedirect to="https://synesthetic.ca/blog/" />} />
                      <Route path="/tags" element={<ExternalRedirect to="https://synesthetic.ca/blog/" />} />
                      <Route path="/tags/*" element={<ExternalRedirect to="https://synesthetic.ca/blog/" />} />
                      <Route path="/authors" element={<ExternalRedirect to="https://synesthetic.ca/blog/" />} />
                      <Route path="/authors/*" element={<ExternalRedirect to="https://synesthetic.ca/blog/" />} />
                      <Route path="/posts" element={<ExternalRedirect to="https://synesthetic.ca/blog/" />} />
                      <Route path="/posts/*" element={<ExternalRedirect to="https://synesthetic.ca/blog/" />} />
                      <Route path="/tools" element={<ExternalRedirect to="https://synesthetic.ca/" />} />
                      <Route path="/tools/*" element={<ExternalRedirect to="https://synesthetic.ca/" />} />
                      <Route path="/page" element={<ExternalRedirect to="https://synesthetic.ca/blog/" />} />
                      <Route path="/page/*" element={<ExternalRedirect to="https://synesthetic.ca/blog/" />} />
                      <Route path="/index.xml" element={<ExternalRedirect to="https://synesthetic.ca/blog/" />} />
                      <Route path="/index.json" element={<ExternalRedirect to="https://synesthetic.ca/blog/" />} />
                      {/* Public profile route - must be last to avoid conflicts */}
                      <Route
                        path="/:username"
                        element={<PublicProfilePage />}
                      />
                    </Routes>
                  </Suspense>
                </main>
                <Footer />
              </div>
            </Router>
          </AuthProvider>
        </ToastProvider>
      </ErrorBoundary>
    </HelmetProvider>
  );
}

export default App;

